<template>
  <vx-card class="mt-base" :title="activityTitle">
    <p class="mb-5">
      Knowing your client’s hair type is essential. It’s one of the first things you’ll need to
      assess when meeting a new client, as it will help you decide what the best styles, products,
      and treatments will be for their particular type of hair.
    </p>

    <p class="mb-5">
      When we talk about hair types, we are really talking about five distinct characteristics.
      These characteristics are:
    </p>

    <ol class="numbered-list">
      <li>Density</li>
      <li>Structure</li>
      <li>Texture</li>
      <li>Porosity</li>
      <li>Scalp</li>
    </ol>
    <p class="mb-5">
      Click on each of the tiles below to read the information (and answer your type) and then
      complete the activities below.
    </p>

    <vs-row vs-align="center" vs-type="flex" vs-w="12" class="mt-5 mb-10">
      <vs-col vs-lg="4" vs-sm="4" vs-xs="12">
        <router-link
          :to="
            '/student/work-experience/' + this.$route.params.id + '/activities/hair-types-density'
          "
        >
          <img
            src="@/assets/images/experiences/hairdressing/density_tile.jpg"
            class="img-responsive px-5 mx-auto"
            alt="Hair types: density"
          />
        </router-link>
      </vs-col>
      <vs-col vs-lg="4" vs-sm="4" vs-xs="12">
        <router-link
          :to="
            '/student/work-experience/' + this.$route.params.id + '/activities/hair-types-structure'
          "
        >
          <img
            src="@/assets/images/experiences/hairdressing/structure_tile.jpg"
            class="img-responsive px-5 mx-auto"
            alt="Hair types: structure"
          />
        </router-link>
      </vs-col>
      <vs-col vs-lg="4" vs-sm="4" vs-xs="12">
        <router-link
          :to="
            '/student/work-experience/' + this.$route.params.id + '/activities/hair-types-texture'
          "
        >
          <img
            src="@/assets/images/experiences/hairdressing/texture_tile.jpg"
            class="img-responsive px-5 mx-auto"
            alt="Hair types: texture"
          />
        </router-link>
      </vs-col>
    </vs-row>
    <vs-row vs-align="center" vs-type="flex" vs-w="12" class="my-5">
      <vs-col vs-lg="4" vs-sm="4" vs-xs="12">
        <router-link
          :to="
            '/student/work-experience/' + this.$route.params.id + '/activities/hair-types-porosity'
          "
        >
          <img
            src="@/assets/images/experiences/hairdressing/porosity_tile.jpg"
            class="img-responsive px-5 mx-auto"
            alt="Hair types: porosity"
          />
        </router-link>
      </vs-col>
      <vs-col vs-lg="4" vs-sm="4" vs-xs="12">
        <router-link
          :to="
            '/student/work-experience/' +
            this.$route.params.id +
            '/activities/hair-types-scalp-hair'
          "
        >
          <img
            src="@/assets/images/experiences/hairdressing/scalp_tile.jpg"
            class="img-responsive px-5 mx-auto"
            alt="Hair types: scalp hair"
          />
        </router-link>
      </vs-col>
    </vs-row>

    <h4 class="my-5"><u>Task 1: Draw Up Personal Hair Treatment Plan</u></h4>

    <p class="mb-5">Now, you’ve classified your hair according to the following characteristics:</p>
    <ul class="default-list">
      <li>Density</li>
      <li>Structure</li>
      <li>Texture</li>
      <li>Porosity</li>
      <li>Scalp</li>
    </ul>
    <p class="mb-5">
      Can you draw up a treatment plan based on the advice in each section? Write some notes below:
    </p>
    <textarea-block v-model="data.question1" :editable="isEditable" />

    <h4 class="mb-5"><u>Task 2: Identifying Other People’s Hair Types</u></h4>

    <p class="mb-5">
      Now you’ve had some practice categorising your hair type; it’s time to use what you’ve learned
      to identify another person’s hair type.
    </p>
    <p class="mb-5">
      Using the information above, can you identify the density, structure, and texture of a
      friend’s, family member’s hair?
    </p>
    <p class="mb-5">Record their information below:</p>
    <p class="mb-5">Name:</p>
    <text-block v-model="data.question2" :editable="isEditable" />

    <p class="mb-5">
      Assess their hair according to the following criteria and record your assessment by ticking
      the appropriate box.
    </p>

    <div v-for="(question, index) in questions" :key="index">
      <h5 class="mb-5 questionlabel">{{ question.question }}</h5>
      <div class="vx-row my-5">
        <div
          class="vx-col w-full mb-2"
          v-for="(answer, index2) in question.answers"
          :key="index2"
          :class="{ wrong1: !answer.correct && answers['question' + index] == answer.val }"
          v-if="isEditable"
        >
          <vs-radio
            :vs-name="'answers-question-' + index"
            v-model="answers['question' + index]"
            class="checkbox_answer multiline"
            :vs-value="answer.val"
            >{{ answer.answer }}</vs-radio
          >
        </div>
        <p
          class="my-2 mx-5"
          :class="{
            correct: answers['question' + index] == answer.val,
          }"
          v-else
        >
          {{ answer.answer }}
        </p>
      </div>
    </div>

    <p class="mb-5">Now, draw up a brief treatment plan for their hair below:</p>
    <textarea-block v-model="data.question4" :editable="isEditable" />

    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';
export default {
  data() {
    return {
      file: '',
      questions: {
        1: {
          question: 'Hair Density',
          answers: [
            { val: 1, answer: 'Low Density', correct: true },
            { val: 2, answer: 'Medium Density', correct: false },
            { val: 3, answer: 'High Density', correct: false },
          ],
        },
        2: {
          question: 'Hair Structure',
          answers: [
            { val: 4, answer: 'Straight', correct: true },
            { val: 5, answer: 'Wavy', correct: false },
            { val: 6, answer: 'Curly', correct: false },
            { val: 6, answer: 'Coily', correct: false },
          ],
        },
        3: {
          question: 'Hair Porosity',
          answers: [
            { val: 7, answer: 'High', correct: true },
            { val: 8, answer: 'Medium', correct: false },
            { val: 9, answer: 'Low', correct: false },
          ],
        },
        4: {
          question: 'Scalp',
          answers: [
            { val: 10, answer: 'Oily', correct: true },
            { val: 11, answer: 'Medium', correct: false },
            { val: 12, answer: 'Dry', correct: false },
          ],
        },
      },
      answers: {
        question1: null,
        question2: null,
        question3: null,
        question4: null,
      },
    };
  },
  methods: {
    save() {
      this.data.question3 = JSON.stringify(this.answers);
      this.saveActivity(this.$route.params.slug, this.data, this.file, 'doc');
    },
  },
  computed: computed,
  inject: inject,
  mounted() {
    if (this.data.question3) {
      this.answers = JSON.parse(this.data.question3);
    }
  },
};
</script>

<style scoped>
.wrong {
  background: rgba(202, 20, 20, 0.24);
  border-bottom: 1px solid #f8f8f8;
  padding-top: 2px;
}
.correct {
  background: rgba(75, 202, 20, 0.24);
  border-bottom: 1px solid #f8f8f8;
}
</style>
